import { useState } from 'react'
import { api, errorMessage, useQuery } from '../api.js'
import {
  Alert,
  Button,
  Medidor,
  PageHeader,
  Panel,
  Pill,
  Sparkline,
  bytesHumano,
  uptimeHumano,
} from '../components/ui.js'
import { ConectarServidor } from '../components/ConectarServidor.js'
import { StepUpModal, type StepUpRequest } from '../components/StepUpModal.js'

type Servidor = Awaited<ReturnType<typeof api.fleet.servers.query>>[number]

/**
 * Monitor — a visao geral e moderna do servidor.
 *
 * Um cartao por maquina, com CPU/memoria/disco de relance e o estado do
 * agente. Clicar abre os graficos das ultimas 24 h. A pergunta que esta tela
 * responde num segundo e "esta tudo bem la?", sem abrir RDP em nada.
 *
 * Sem agente instalado, o cartao diz isso — a coleta vem do agente, e a tela
 * nao inventa numero que nao tem.
 */
export function Monitor({ onNavigate }: { onNavigate: (tab: 'frota' | 'contas') => void }) {
  const servers = useQuery(() => api.fleet.servers.query(), [], { pollMs: 15_000 })
  const metrics = useQuery(() => api.identity.metrics.query(), [], { pollMs: 15_000 })
  const agentes = useQuery(() => api.identity.agents.query(), [], { pollMs: 15_000 })
  const [aberto, setAberto] = useState<string | null>(null)

  const [conectar, setConectar] = useState(false)
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null)
  const lista = servers.data ?? []
  const porServidor = metrics.data ?? {}
  const agentesLista = agentes.data ?? []
  const agentePorServidor = new Map(agentesLista.map((a) => [a.serverId, a]))
  const algumAgenteOnline = agentesLista.some((a) => a.status === 'online')

  return (
    <div className="space-y-6">
      <PageHeader
        title="Monitor"
        subtitle="A visão geral da frota: CPU, memória, disco e o estado do agente de cada servidor, ao vivo."
        action={
          <Button variant="primary" onClick={() => setConectar(true)}>
            Conectar servidor
          </Button>
        }
      />

      <PrimeirosPassos
        temServidor={lista.length > 0}
        temAgente={algumAgenteOnline}
        onNavigate={onNavigate}
        onConectar={() => setConectar(true)}
      />

      {conectar && <ConectarServidor onClose={() => setConectar(false)} />}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {lista.map((s) => (
          <CartaoServidor
            key={s.id}
            servidor={s}
            metrica={porServidor[s.id]}
            agenteOnline={agentePorServidor.get(s.id)?.status === 'online'}
            aberto={aberto === s.id}
            onAbrir={() => setAberto(aberto === s.id ? null : s.id)}
            onStepUp={setStepUp}
          />
        ))}
      </div>

      {stepUp && <StepUpModal request={stepUp} onClose={() => setStepUp(null)} />}
    </div>
  )
}

/**
 * Primeiros passos — o guia que faltava.
 *
 * O relato foi "simplesmente nao funciona a criacao". Quase sempre isso e:
 * cheguei numa tela e nao sabia o proximo passo. Este bloco mostra a trilha
 * inteira, marca o que ja esta feito, e leva ao lugar certo com um clique.
 * Some quando tudo esta pronto — nao vira enfeite permanente.
 */
function PrimeirosPassos({
  temAgente,
  onNavigate,
  onConectar,
}: {
  temServidor: boolean
  temAgente: boolean
  onNavigate: (tab: 'frota' | 'contas') => void
  onConectar: () => void
}) {
  if (temAgente) return null

  const passos = [
    {
      feito: temAgente,
      titulo: 'Conectar um servidor',
      texto: 'Um comando na máquina e um clique de aprovação aqui — o servidor entra na frota já monitorado, sem token nem cadastro antes.',
      acao: onConectar,
      rotulo: 'Conectar servidor',
    },
    {
      feito: false,
      titulo: 'Criar e gerenciar contas',
      texto: 'Com o agente online, criar usuário, trocar senha e administrar acesso passa a funcionar pela tela.',
      acao: () => onNavigate('contas'),
      rotulo: 'Abrir Contas',
    },
  ]
  const proximo = passos.findIndex((p) => !p.feito)

  return (
    <Panel title="Primeiros passos">
      <ol className="space-y-3">
        {passos.map((p, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                p.feito ? 'bg-ok-soft text-ok' : i === proximo ? 'bg-accent text-white' : 'bg-ground text-ink-3'
              }`}
            >
              {p.feito ? '✓' : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink">{p.titulo}</div>
              <div className="text-xs text-ink-3">{p.texto}</div>
            </div>
            {i === proximo && (
              <Button variant="primary" onClick={p.acao}>
                {p.rotulo}
              </Button>
            )}
          </li>
        ))}
      </ol>
    </Panel>
  )
}

function CartaoServidor({
  servidor,
  metrica,
  agenteOnline,
  aberto,
  onAbrir,
  onStepUp,
}: {
  servidor: Servidor
  metrica?: {
    cpuPercent: number | null
    memPercent: number | null
    diskPercent: number | null
    memTotal: string | null
    memUsada: string | null
    uptimeSec: number | null
    discos: Array<{ ponto: string; total: number; usado: number; percent: number }>
    idadeMin: number
  }
  agenteOnline: boolean
  aberto: boolean
  onAbrir: () => void
  onStepUp: (r: StepUpRequest) => void
}) {
  // Metrica de mais de 3 min nao e "ao vivo": o agente coleta a cada 30 s.
  const fresca = metrica && metrica.idadeMin < 3
  const semAgente = !agenteOnline && !metrica

  return (
    <section className="rounded border border-line bg-panel shadow-sm">
      <button onClick={onAbrir} className="w-full border-b border-line px-4 py-3 text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium text-ink">{servidor.hostname}</span>
          {agenteOnline ? (
            <Pill tone="ok">agente online</Pill>
          ) : metrica ? (
            <Pill tone="warn">agente offline</Pill>
          ) : (
            <Pill tone="neutral">sem agente</Pill>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-3">
          <span className="font-mono">{servidor.publicIp ?? '—'}</span>
          {metrica?.uptimeSec != null && <span>· no ar {uptimeHumano(metrica.uptimeSec)}</span>}
        </div>
      </button>

      <div className="space-y-3 p-4">
        {semAgente ? (
          <p className="text-sm text-ink-3">
            Sem agente instalado. Instale-o na aba Contas para ver CPU, memoria e disco daqui.
          </p>
        ) : !metrica ? (
          <p className="text-sm text-ink-3">Aguardando a primeira coleta...</p>
        ) : (
          <>
            {!fresca && (
              <p className="text-xs text-warn">
                Ultima coleta ha {metrica.idadeMin} min — o agente pode ter caido.
              </p>
            )}
            <Medidor label="CPU" valor={metrica.cpuPercent} />
            <Medidor label="Memoria" valor={metrica.memPercent} />
            {metrica.memTotal && (
              <p className="text-right text-[11px] text-ink-3">
                {bytesHumano(metrica.memUsada)} / {bytesHumano(metrica.memTotal)}
              </p>
            )}
            <Medidor label="Disco (mais cheio)" valor={metrica.diskPercent} />
          </>
        )}
      </div>

      {aberto && metrica && (
        <DetalheMonitor
          servidor={servidor}
          discos={metrica.discos}
          agenteOnline={agenteOnline}
          onStepUp={onStepUp}
        />
      )}
    </section>
  )
}

function DetalheMonitor({
  servidor,
  discos,
  agenteOnline,
  onStepUp,
}: {
  servidor: Servidor
  discos: Array<{ ponto: string; total: number; usado: number; percent: number }>
  agenteOnline: boolean
  onStepUp: (r: StepUpRequest) => void
}) {
  const serverId = servidor.id
  const hist = useQuery(() => api.identity.metricsHistory.query({ serverId, horas: 24 }), [serverId])
  const pontos = hist.data ?? []

  return (
    <div className="space-y-4 border-t border-line bg-ground/40 p-4">
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
          CPU — ultimas 24h
        </div>
        <Sparkline pontos={pontos.map((p) => p.cpu)} cor="var(--color-accent)" />
      </div>
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Memoria — ultimas 24h
        </div>
        <Sparkline pontos={pontos.map((p) => p.mem)} cor="var(--color-ok)" />
      </div>

      {discos.length > 0 && (
        <div>
          <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-ink-3">Discos</div>
          <div className="space-y-2">
            {discos.map((d) => (
              <div key={d.ponto}>
                <Medidor label={d.ponto} valor={d.percent} />
                <p className="text-right text-[11px] text-ink-3">
                  {bytesHumano(d.usado)} / {bytesHumano(d.total)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {pontos.length === 0 && (
        <p className="text-xs text-ink-3">Sem historico ainda — os graficos aparecem apos algumas coletas.</p>
      )}

      <UsoAoVivo serverId={serverId} />
      <ProgramasInstalados serverId={serverId} />
      <AgendamentoEnergia servidor={servidor} onStepUp={onStepUp} />
      <GerenciarServidor serverId={serverId} agenteOnline={agenteOnline} onStepUp={onStepUp} />
      <Certificados serverId={serverId} agenteOnline={agenteOnline} onStepUp={onStepUp} />
    </div>
  )
}

/**
 * Certificados da Loja da Máquina — liberar/revogar a chave para um grupo.
 *
 * O caso e-CAC: o certificado fica no servidor e vários usuários assinam/
 * autenticam com ele. Você digita o grupo do Windows e libera/revoga a chave
 * privada; depois controla quem assina pela pertença ao grupo (Contas →
 * Grupos). Owner + motivo, e vai para a Auditoria.
 */
function Certificados({
  serverId,
  agenteOnline,
  onStepUp,
}: {
  serverId: string
  agenteOnline: boolean
  onStepUp: (r: StepUpRequest) => void
}) {
  const certs = useQuery(() => api.identity.certList.query({ serverId }), [serverId])
  const [grupo, setGrupo] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  if (!agenteOnline) return null
  const lista = certs.data ?? []

  function acao(thumbprint: string, subject: string, conceder: boolean) {
    const g = grupo.trim()
    if (!g) {
      setErro('Digite o nome do grupo do Windows primeiro.')
      return
    }
    onStepUp({
      action: 'os.cert.grant',
      title: `${conceder ? 'Liberar' : 'Revogar'} certificado — grupo ${g}`,
      description: `${conceder ? 'Concede' : 'Remove'} a leitura da chave privada de "${
        subject || thumbprint
      }" ${conceder ? 'para' : 'do'} grupo ${g}. Quem estiver no grupo poderá usar o certificado (e-CAC/assinatura).`,
      requireReason: true,
      onProved: async (proofId) => {
        setOcupado(true)
        setErro(null)
        setMsg(null)
        try {
          if (conceder) {
            await api.identity.certGrantKey.mutate({ stepUpProofId: proofId, serverId, thumbprint, grupo: g })
          } else {
            await api.identity.certRevokeKey.mutate({ stepUpProofId: proofId, serverId, thumbprint, grupo: g })
          }
          setMsg(`${conceder ? 'Liberado' : 'Revogado'} para o grupo ${g}.`)
        } catch (e) {
          setErro(errorMessage(e))
        } finally {
          setOcupado(false)
        }
      },
    })
  }

  return (
    <div className="space-y-3 border-t border-line pt-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        Certificados (e-CAC) — Loja da Máquina
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={grupo}
          onChange={(e) => setGrupo(e.target.value)}
          placeholder="grupo do Windows — ex.: AssinantesVia"
          className="w-56 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none"
        />
        <span className="text-[11px] text-ink-3">
          libera/revoga a chave para este grupo; depois gerencie quem assina em Contas → Grupos.
        </span>
      </div>
      {lista.length === 0 ? (
        <p className="text-xs text-ink-3">
          Nenhum certificado na Loja da Máquina (ou o servidor não é Windows). Certificado que está
          na conta de um usuário (CurrentUser) não aparece — mova-o para a Loja da Máquina primeiro.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {lista.map((c) => (
            <li key={c.thumbprint} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-ink">{c.subject || '(sem CN)'}</span>
              <span className="font-mono text-ink-3">{c.thumbprint.slice(0, 16)}…</span>
              {c.hasKey ? <Pill tone="ok">com chave</Pill> : <Pill tone="neutral">sem chave</Pill>}
              <div className="ml-auto flex gap-1">
                <Button
                  variant="ghost"
                  disabled={!grupo.trim() || ocupado || !c.hasKey}
                  onClick={() => acao(c.thumbprint, c.subject, true)}
                >
                  liberar
                </Button>
                <Button
                  variant="ghost"
                  disabled={!grupo.trim() || ocupado}
                  onClick={() => acao(c.thumbprint, c.subject, false)}
                >
                  revogar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {msg && <p className="text-xs text-ok">{msg}</p>}
      {erro && <Alert>{erro}</Alert>}
    </div>
  )
}

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const ACAO_ENERGIA: Record<'on' | 'off' | 'restart', string> = {
  on: 'Ligar',
  off: 'Desligar',
  restart: 'Reiniciar',
}

/**
 * Agendamento de energia DESTE servidor — liga/desliga por horário.
 *
 * O mesmo motor da aba Frota, mas no card do servidor: cria, pausa e remove
 * agendamentos só desta máquina. Criar pede 2FA (autoriza a recorrência); o
 * motor dispara sozinho no horário (fuso de São Paulo). Só servidor vindo de
 * provedor (Kamatera/Hostinger) liga/desliga por API — por agente, não.
 */
function AgendamentoEnergia({
  servidor,
  onStepUp,
}: {
  servidor: Servidor
  onStepUp: (r: StepUpRequest) => void
}) {
  const agendas = useQuery(() => api.fleet.schedules.query(), [], { pollMs: 30_000 })
  const [action, setAction] = useState<'on' | 'off' | 'restart'>('off')
  const [hora, setHora] = useState('22:00')
  const [dias, setDias] = useState<number[]>([1, 2, 3, 4, 5])
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const temProvedor = Boolean(servidor.provider)
  const doServidor = (agendas.data ?? []).filter((a) => a.serverId === servidor.id)
  const podeAcao =
    action === 'on' ? servidor.can.on : action === 'off' ? servidor.can.off : servidor.can.restart
  const horaOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(hora)
  const podeCriar = temProvedor && podeAcao && horaOk && dias.length > 0 && !ocupado

  const toggleDia = (d: number) =>
    setDias((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d].sort()))

  async function comAcao(fn: () => Promise<unknown>) {
    setOcupado(true)
    setErro(null)
    try {
      await fn()
      await agendas.refetch()
    } catch (e) {
      setErro(errorMessage(e))
    } finally {
      setOcupado(false)
    }
  }

  function criar() {
    onStepUp({
      action: 'server.schedule.set',
      title: `Agendar: ${ACAO_ENERGIA[action]} ${servidor.hostname}`,
      description: `${ACAO_ENERGIA[action]} às ${hora}, ${dias
        .map((d) => DIAS_SEMANA[d])
        .join(', ')} (fuso de São Paulo). O motor dispara sozinho — você autoriza a recorrência agora.`,
      onProved: async (proofId) => {
        await comAcao(() =>
          api.fleet.addSchedule.mutate({
            stepUpProofId: proofId,
            serverId: servidor.id,
            action,
            timeHhmm: hora,
            weekdays: dias,
          }),
        )
      },
    })
  }

  return (
    <div className="space-y-3 border-t border-line pt-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        Agendamento de energia
      </div>
      {erro && <Alert>{erro}</Alert>}

      {doServidor.length > 0 && (
        <ul className="space-y-1.5">
          {doServidor.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2 text-xs">
              <Pill tone={a.action === 'on' ? 'ok' : a.action === 'off' ? 'crit' : 'warn'}>
                {ACAO_ENERGIA[a.action]}
              </Pill>
              <span className="font-mono text-ink-2">{a.timeHhmm}</span>
              <span className="text-ink-3">{a.weekdays.map((d) => DIAS_SEMANA[d]).join(' ')}</span>
              {!a.enabled && <Pill tone="neutral">pausado</Pill>}
              <div className="ml-auto flex gap-1">
                <Button
                  variant="ghost"
                  disabled={ocupado}
                  onClick={() =>
                    comAcao(() => api.fleet.toggleSchedule.mutate({ id: a.id, enabled: !a.enabled }))
                  }
                >
                  {a.enabled ? 'pausar' : 'ativar'}
                </Button>
                <Button
                  variant="ghost"
                  disabled={ocupado}
                  onClick={() => comAcao(() => api.fleet.deleteSchedule.mutate({ id: a.id }))}
                >
                  excluir
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!temProvedor ? (
        <p className="text-xs text-ink-3">
          Este servidor é por agente (sem provedor com API de energia), então não liga/desliga
          sozinho por aqui. Agendamento de energia vale para Kamatera/Hostinger.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value as typeof action)}
            className="rounded-lg border border-line bg-panel px-2 py-1.5 text-sm text-ink focus:border-accent focus:outline-none"
          >
            <option value="off">Desligar</option>
            <option value="on">Ligar</option>
            <option value="restart">Reiniciar</option>
          </select>
          <input
            value={hora}
            onChange={(e) => setHora(e.target.value)}
            placeholder="22:00"
            className="w-20 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink focus:border-accent focus:outline-none"
          />
          <div className="flex gap-1">
            {DIAS_SEMANA.map((rot, d) => (
              <button
                key={d}
                onClick={() => toggleDia(d)}
                className={`h-8 w-8 rounded-lg border text-[11px] ${
                  dias.includes(d)
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-line text-ink-3 hover:border-accent'
                }`}
              >
                {rot}
              </button>
            ))}
          </div>
          <Button variant="primary" onClick={criar} disabled={!podeCriar}>
            Agendar
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Instalar programa e controlar serviço — os facilitadores, com step-up.
 *
 * Verbos fechados no agente: instala por ID de catálogo (winget/choco/apt) e
 * liga/para/reinicia um serviço pelo nome. Cada ação pede reconfirmação (roda
 * como administrador) e vai para a Auditoria.
 */
function GerenciarServidor({
  serverId,
  agenteOnline,
  onStepUp,
}: {
  serverId: string
  agenteOnline: boolean
  onStepUp: (r: StepUpRequest) => void
}) {
  const [pacote, setPacote] = useState('')
  const [servico, setServico] = useState('')
  const [caminho, setCaminho] = useState('')
  const [quem, setQuem] = useState('')
  const [nivel, setNivel] = useState<'read' | 'modify' | 'full'>('read')
  const [tipoP, setTipoP] = useState<'user' | 'group'>('group')
  const [msg, setMsg] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const podeAgir = agenteOnline && !ocupado

  function pkg(remover: boolean) {
    const nome = pacote.trim()
    if (!nome) return
    onStepUp({
      action: 'os.pkg.install',
      title: `${remover ? 'Remover' : 'Instalar'} ${nome}`,
      description: remover
        ? 'Remove o programa pelo gerenciador de pacotes do host.'
        : 'Instala o programa pelo catálogo (winget/Chocolatey no Windows, apt no Linux). Roda como administrador.',
      onProved: async (proofId) => {
        setOcupado(true)
        setErro(null)
        setMsg(null)
        try {
          if (remover) {
            await api.identity.uninstallPackage.mutate({ stepUpProofId: proofId, serverId, pacote: nome })
          } else {
            await api.identity.installPackage.mutate({ stepUpProofId: proofId, serverId, pacote: nome })
          }
          setMsg(`${remover ? 'Removido' : 'Instalado'}: ${nome}`)
          setPacote('')
        } catch (e) {
          setErro(errorMessage(e))
        } finally {
          setOcupado(false)
        }
      },
    })
  }

  function svc(acao: 'start' | 'stop' | 'restart') {
    const nome = servico.trim()
    if (!nome) return
    const label = { start: 'Iniciar', stop: 'Parar', restart: 'Reiniciar' }[acao]
    onStepUp({
      action: 'os.service.control',
      title: `${label} serviço ${nome}`,
      description: `${label} o serviço "${nome}" no host.`,
      onProved: async (proofId) => {
        setOcupado(true)
        setErro(null)
        setMsg(null)
        try {
          await api.identity.serviceControl.mutate({ stepUpProofId: proofId, serverId, servico: nome, acao })
          setMsg(`Serviço ${nome}: ${label.toLowerCase()} OK`)
        } catch (e) {
          setErro(errorMessage(e))
        } finally {
          setOcupado(false)
        }
      },
    })
  }

  function pasta(conceder: boolean) {
    const p = caminho.trim()
    const q = quem.trim()
    if (!p || !q) return
    const rot = { read: 'leitura', modify: 'leitura e edição', full: 'controle total' }[nivel]
    onStepUp({
      action: 'os.acl.grant',
      title: `${conceder ? 'Liberar' : 'Remover'} acesso — ${q}`,
      description: conceder
        ? `Concede ${rot} de "${p}" para ${tipoP === 'group' ? 'o grupo' : 'o usuário'} ${q}.`
        : `Remove o acesso de ${q} a "${p}".`,
      onProved: async (proofId) => {
        setOcupado(true)
        setErro(null)
        setMsg(null)
        try {
          await api.identity.folderAccess.mutate({
            stepUpProofId: proofId,
            serverId,
            path: p,
            principal: q,
            tipo: tipoP,
            nivel,
            conceder,
          })
          setMsg(`${conceder ? 'Liberado' : 'Removido'}: ${q} → ${p}`)
        } catch (e) {
          setErro(errorMessage(e))
        } finally {
          setOcupado(false)
        }
      },
    })
  }

  const inputCls =
    'flex-1 min-w-0 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none'
  const selCls =
    'rounded-lg border border-line bg-panel px-2 py-1.5 text-sm text-ink focus:border-accent focus:outline-none'

  return (
    <div className="space-y-4 border-t border-line pt-4">
      {!agenteOnline && (
        <p className="text-xs text-ink-3">Precisa do agente online para instalar programas ou mexer em serviços.</p>
      )}
      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Instalar programa
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            value={pacote}
            onChange={(e) => setPacote(e.target.value)}
            placeholder="ID do pacote — ex.: Google.Chrome, 7zip.7zip, htop"
            className={inputCls}
          />
          <Button onClick={() => pkg(false)} disabled={!pacote.trim() || !podeAgir} variant="primary">
            Instalar
          </Button>
          <Button onClick={() => pkg(true)} disabled={!pacote.trim() || !podeAgir} variant="ghost">
            Remover
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-ink-3">
          Windows: winget (ex.: <span className="font-mono">Google.Chrome</span>). Linux: apt (ex.:{' '}
          <span className="font-mono">htop</span>). Aparece no inventário após instalar.
        </p>
      </div>

      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Controlar serviço
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            value={servico}
            onChange={(e) => setServico(e.target.value)}
            placeholder="nome do serviço"
            className={inputCls}
          />
          <Button onClick={() => svc('start')} disabled={!servico.trim() || !podeAgir}>
            Iniciar
          </Button>
          <Button onClick={() => svc('restart')} disabled={!servico.trim() || !podeAgir}>
            Reiniciar
          </Button>
          <Button onClick={() => svc('stop')} disabled={!servico.trim() || !podeAgir} variant="danger">
            Parar
          </Button>
        </div>
      </div>

      <div>
        <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Permissão de pasta
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            value={caminho}
            onChange={(e) => setCaminho(e.target.value)}
            placeholder={'caminho da pasta — ex.: D:\\Compartilhado ou /srv/dados'}
            className={inputCls}
          />
          <input
            value={quem}
            onChange={(e) => setQuem(e.target.value)}
            placeholder="usuário ou grupo"
            className="w-40 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none"
          />
          <select value={tipoP} onChange={(e) => setTipoP(e.target.value as typeof tipoP)} className={selCls}>
            <option value="group">Grupo</option>
            <option value="user">Usuário</option>
          </select>
          <select value={nivel} onChange={(e) => setNivel(e.target.value as typeof nivel)} className={selCls}>
            <option value="read">Ler</option>
            <option value="modify">Ler e editar</option>
            <option value="full">Controle total</option>
          </select>
          <Button onClick={() => pasta(true)} disabled={!caminho.trim() || !quem.trim() || !podeAgir} variant="primary">
            Liberar
          </Button>
          <Button onClick={() => pasta(false)} disabled={!caminho.trim() || !quem.trim() || !podeAgir} variant="ghost">
            Remover
          </Button>
        </div>
      </div>

      {msg && <p className="text-xs text-ok">{msg}</p>}
      {erro && <Alert>{erro}</Alert>}
    </div>
  )
}

/**
 * Uso ao vivo: os processos que mais consomem agora.
 *
 * Responde "o que esta pesando neste servidor" sem abrir o gerenciador de
 * tarefas por RDP. O agente manda o topo a cada minuto; a tela so mostra.
 */
function UsoAoVivo({ serverId }: { serverId: string }) {
  const uso = useQuery(() => api.identity.processes.query({ serverId }), [serverId], {
    pollMs: 30_000,
  })
  const d = uso.data

  return (
    <div>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-ink-3">
        Uso agora{d ? ` — ${d.totalProcessos} processos` : ''}
      </div>
      {!d ? (
        <p className="text-xs text-ink-3">
          Sem dados de uso ainda — o agente coleta a cada minuto assim que conecta.
        </p>
      ) : d.itens.length === 0 ? (
        <p className="text-xs text-ink-3">Nenhum processo no ultimo retrato.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-ink-3">
              <th className="pb-1">Processo</th>
              <th className="pb-1 text-right">CPU</th>
              <th className="pb-1 text-right">Memoria</th>
            </tr>
          </thead>
          <tbody>
            {d.itens.map((p) => (
              <tr key={p.pid} className="border-t border-line/50">
                <td className="py-1 font-mono text-ink">{p.nome}</td>
                <td className="py-1 text-right text-ink-2">{Math.round(p.cpuPercent)}%</td>
                <td className="py-1 text-right text-ink-2">
                  {bytesHumano(p.memBytes)} · {Math.round(p.memPercent)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Programas instalados neste servidor, com filtro.
 *
 * O inventario e um retrato do agente (a cada 6 h); a lista pode ser longa,
 * por isso rola dentro de uma caixa e tem busca. Vale para Windows (registro)
 * e Linux (pacotes) — a mesma tela, a mesma pergunta.
 */
function ProgramasInstalados({ serverId }: { serverId: string }) {
  const inv = useQuery(() => api.identity.software.query({ serverId }), [serverId])
  const [busca, setBusca] = useState('')
  const d = inv.data
  const itens = (d?.itens ?? []).filter((i) =>
    busca.trim() === ''
      ? true
      : `${i.nome} ${i.fabricante ?? ''}`.toLowerCase().includes(busca.toLowerCase()),
  )

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Programas instalados{d ? ` — ${d.total}` : ''}
        </div>
        {d && d.total > 0 && (
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="filtrar..."
            className="rounded border border-line bg-panel px-2 py-1 text-xs text-ink focus:border-accent focus:outline-none"
          />
        )}
      </div>
      {!d || d.total === 0 ? (
        <p className="text-xs text-ink-3">
          Nenhum inventario ainda — o agente envia a lista logo apos conectar, e depois a cada 6 h.
        </p>
      ) : (
        <div className="max-h-72 overflow-y-auto rounded border border-line/60">
          <table className="w-full text-xs">
            <tbody>
              {itens.map((i, idx) => (
                <tr key={`${i.nome}-${idx}`} className="border-b border-line/40 last:border-0">
                  <td className="px-2 py-1 text-ink">{i.nome}</td>
                  <td className="px-2 py-1 font-mono text-ink-3">{i.versao ?? ''}</td>
                  <td className="px-2 py-1 text-ink-3">{i.fabricante ?? ''}</td>
                </tr>
              ))}
              {itens.length === 0 && (
                <tr>
                  <td className="px-2 py-2 text-ink-3">Nada corresponde ao filtro.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
